import type { RescheduleSuggestionDTO } from 'src/types/dto/reschedule-suggestion.dto';

export interface RescheduleSuggestionViewModel {
  suggestion: RescheduleSuggestionDTO;
  isLoading: boolean;
}

export const getEnrichmentKey = (
  suggestion: RescheduleSuggestionDTO,
  currentActivityId: number
) =>
  [
    suggestion.solutionId,
    currentActivityId,
    suggestion.inspector?.id ?? 'none',
    suggestion.changes
      .map((change) =>
        `${change.activity.id}:${change.newAppointment
          .map(
            (appointment) =>
              `${appointment.from}:${appointment.to}:${appointment.inspector?.id ?? 'none'}`
          )
          .join('|')}`
      )
      .sort((left, right) => left.localeCompare(right))
      .join(','),
  ].join(':');

/**
 * Orders suggestions so that the ones whose data has finished loading from the
 * API appear first (in the order their requests resolved), followed by the
 * suggestions that are still being fetched, in their original arrival order.
 */
export const orderRescheduleSuggestionsByCompletion = (
  suggestions: RescheduleSuggestionDTO[],
  dataById: Map<number, RescheduleSuggestionDTO>,
  completionOrder: number[],
  loadingById: Map<number, boolean>
): RescheduleSuggestionViewModel[] => {
  const completedSet = new Set(completionOrder);

  const completed = completionOrder
    .filter((solutionId) => dataById.has(solutionId))
    .map((solutionId) => ({
      suggestion: dataById.get(solutionId)!,
      isLoading: false,
    }));

  const pending = suggestions
    .filter(
      (suggestion) =>
        !completedSet.has(suggestion.solutionId) && dataById.has(suggestion.solutionId)
    )
    .map((suggestion) => ({
      suggestion: dataById.get(suggestion.solutionId)!,
      isLoading: loadingById.get(suggestion.solutionId) ?? true,
    }));

  return [...completed, ...pending];
};
